package com.sddamico.draftmachine.pages.draft

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.components.layouts.LoadingSection
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Avatar
import com.sddamico.draftmachine.ds.core.AvatarSize
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.core.CardVariant
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.feedback.EmptyState
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.forms.InputSize
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.TransitionRole
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.ds.motion.transitionRole
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleH2
import com.sddamico.draftmachine.models.TeamResponse
import com.sddamico.draftmachine.models.UpdateDraftTeamRequest
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.TeamField
import com.sddamico.draftmachine.util.UpdateDraftTeamResult
import com.sddamico.draftmachine.util.fetchDraftTeams
import com.sddamico.draftmachine.util.rememberApiResource
import com.sddamico.draftmachine.util.updateDraftTeam
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * Lets a draft administrator correct team details entered wrong at setup. Reads the roster from
 * `/api/draft-teams`, the only payload that carries owner emails, so it is shown to administrators
 * only. [onSaved] refreshes the rest of the settings screen, which shows team names too.
 */
@Composable
internal fun DraftTeamsTab(draftId: String, onSaved: () -> Unit) {
  val loaded = rememberApiResource { fetchDraftTeams(draftId) }
  var saved by remember { mutableStateOf<Map<String, TeamResponse>>(emptyMap()) }
  var editingId by remember { mutableStateOf<String?>(null) }

  Card(variant = CardVariant.Raised, riveted = true) {
    Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space3).toAttrs()) {
      Icon("users-three", IconWeight.Bold, Modifier.fontSize(24.px).color(DmColor.patina300))
      SpanText("Teams", Modifier.roleH2().displayBlock())
    }
    SpanText(
      "Fix a team or owner entered wrong at setup. The email is how an owner signs in and gets " +
        "alerts, so changing it hands the team to the new address.",
      Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(top = DmSpacing.space3),
    )
    Div(
      attrs = Modifier.height(1.px).backgroundColor(DmColor.border)
        .margin(topBottom = DmSpacing.space5).toAttrs(),
    ) {}

    when (loaded) {
      is LoadState.Loading -> LoadingSection()
      is LoadState.Failed -> EmptyState(icon = "plugs", title = "Couldn't load teams", message = loaded.message)
      is LoadState.Ready -> Div(
        attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs(),
      ) {
        loaded.value.map { saved[it.id] ?: it }.forEach { team ->
          key(team.id) {
            if (team.id == editingId) {
              EditTeamRow(
                draftId = draftId,
                team = team,
                onCancel = { ViewTransitions.run(Motion.Update) { editingId = null } },
                onSaved = { updated ->
                  ViewTransitions.run(Motion.Update) {
                    saved = saved + (updated.id to updated)
                    editingId = null
                  }
                  onSaved()
                },
              )
            } else {
              TeamRow(
                team = team,
                editDisabled = editingId != null,
                onEdit = { ViewTransitions.run(Motion.Update) { editingId = team.id } },
              )
            }
          }
        }
      }
    }
  }
}

private fun Modifier.teamRowSurface(): Modifier = this
  .padding(topBottom = 10.px, leftRight = 12.px)
  .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
  .backgroundColor(DmColor.surfaceInset).transitionRole(TransitionRole.Item)

@Composable
private fun TeamRow(team: TeamResponse, editDisabled: Boolean, onEdit: () -> Unit) {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px).teamRowSurface().toAttrs()) {
    Avatar(name = team.ownerName, size = AvatarSize.Sm)
    Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
      SpanText(
        team.teamName,
        Modifier.fontFamily(*DmType.body).fontSize(14.px).fontWeight(DmType.weightSemibold)
          .color(DmColor.text).displayBlock(),
      )
      SpanText(
        team.ownerName,
        Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textSecondary)
          .displayBlock().margin(top = 1.px),
      )
      SpanText(
        team.ownerEmail,
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted)
          .displayBlock().margin(top = 1.px)
          .styleModifier { property("overflow-wrap", "anywhere") },
      )
    }
    IconButton(
      icon = "pencil-simple",
      label = "Edit ${team.teamName}",
      variant = ButtonVariant.Ghost,
      size = ButtonSize.Sm,
      disabled = editDisabled,
      onClick = onEdit,
    )
  }
}

@Composable
private fun EditTeamRow(
  draftId: String,
  team: TeamResponse,
  onCancel: () -> Unit,
  onSaved: (TeamResponse) -> Unit,
) {
  val scope = rememberCoroutineScope()
  var teamName by remember { mutableStateOf(team.teamName) }
  var ownerName by remember { mutableStateOf(team.ownerName) }
  var ownerEmail by remember { mutableStateOf(team.ownerEmail) }
  var fieldError by remember { mutableStateOf<Pair<TeamField, String>?>(null) }
  var error by remember { mutableStateOf<String?>(null) }
  var saving by remember { mutableStateOf(false) }

  val emailChanged = ownerEmail.trim().lowercase() != team.ownerEmail
  val unchanged = teamName.trim() == team.teamName && ownerName.trim() == team.ownerName && !emailChanged

  fun errorFor(field: TeamField) = fieldError?.takeIf { it.first == field }?.second

  fun save() {
    if (saving || unchanged) return
    fieldError = null
    error = null
    scope.launch {
      saving = true
      val request = UpdateDraftTeamRequest(draftId, team.id, teamName, ownerName, ownerEmail)
      when (val result = updateDraftTeam(request)) {
        is UpdateDraftTeamResult.Success -> onSaved(result.team)
        is UpdateDraftTeamResult.Invalid -> fieldError = result.field to result.message
        is UpdateDraftTeamResult.Failure -> error = result.message
      }
      saving = false
    }
  }

  // Ids are per-row: the roster repeats these labels, and the DS default (a slug of the label)
  // would point every row's label at the first row's input.
  Div(
    attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(10.px)
      .teamRowSurface().border(1.px, LineStyle.Solid, DmColor.brass600).toAttrs(),
  ) {
    Input(
      label = "Team name", icon = "shield", size = InputSize.Sm, id = "in-team-name-${team.id}",
      value = teamName, error = errorFor(TeamField.TeamName), autoFocus = true,
      onValueChange = { teamName = it; fieldError = null }, onEnter = ::save,
    )
    Input(
      label = "Owner", icon = "user", size = InputSize.Sm, id = "in-owner-${team.id}",
      value = ownerName, error = errorFor(TeamField.OwnerName),
      onValueChange = { ownerName = it; fieldError = null }, onEnter = ::save,
    )
    Input(
      label = "Owner email", icon = "envelope-simple", type = "email", size = InputSize.Sm,
      id = "in-owner-email-${team.id}",
      value = ownerEmail, error = errorFor(TeamField.OwnerEmail),
      hint = if (emailChanged) "${team.ownerEmail} will no longer own this team." else null,
      onValueChange = { ownerEmail = it; fieldError = null }, onEnter = ::save,
    )
    error?.let { SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock()) }
    Div(attrs = Modifier.displayFlex().gap(8.px).margin(top = 2.px).toAttrs()) {
      Button(
        "Cancel", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, disabled = saving,
        modifier = Modifier.flex(1), onClick = onCancel,
      )
      Button(
        "Save team", variant = ButtonVariant.Primary, size = ButtonSize.Sm, icon = "floppy-disk",
        loading = saving, disabled = unchanged, modifier = Modifier.flex(1), onClick = ::save,
      )
    }
  }
}
